import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useInviteCustomer } from '../../features/customers/api';
import { copyToClipboard } from '../../utils/clipboard';
import './customers.scss';

interface InviteCustomerModalProps {
  onClose?: () => void;
  onInvited?: () => void;
}

export default function InviteCustomerModal({ onClose, onInvited }: InviteCustomerModalProps) {
  const [email, setEmail] = useState('');
  const [agreementReq, setAgreementReq] = useState(true);
  const [resaleReq, setResaleReq] = useState(true);
  const [name, setName] = useState('');
  const invite = useInviteCustomer();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // lock scroll while open
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  const submit = async () => {
    const trimmed = email.trim();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error('Enter a valid email.');
      return;
    }
    if (!name.trim()) {
      toast.error("Enter the customer's name.");
      return;
    }
    try {
      const resp = await invite.mutateAsync({
        email: trimmed,
        isAgreementRequered: agreementReq,
        isResaleCertificateRequered: resaleReq,
        name: name.trim(),
      });
      toast.success('Invite sent.');
      if (resp?.inviteUrl) {
        const copied = await copyToClipboard(resp.inviteUrl);

        if (copied) {
          toast.info('Invite link copied to clipboard.');
        } else {
          // The link isn't shown anywhere else, so hand it over rather than
          // let a failed copy swallow it. Stays until dismissed.
          toast.warn(
            <span>
              Could not copy the invite link. Copy it manually:
              <br />
              <span className="invite-link-fallback">{resp.inviteUrl}</span>
            </span>,
            { autoClose: false }
          );
        }
      }
      onInvited?.();
    } catch (e) {
      console.error(e);
      toast.error('Failed to send invite.');
    }
  };

  const onBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose?.();
    if (e.key === 'Enter') submit();
  };

  return (
    <div
      className="modal"
      onClick={onBackdropClick}
      onKeyDown={onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-title"
    >
      <div className="modal__backdrop" onClick={onClose} />
      <div ref={panelRef} className="modal__panel" style={{ maxWidth: 520, width: '100%' }}>
        <div className="modal__header">
          <h3 id="invite-title" className="modal__title">
            Invite Customer
          </h3>
          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal__body">
          <div className="grid" style={{ gap: 12 }}>
            <div className="form-control">
              <label>Email</label>
              <input
                type="email"
                placeholder="customer@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-control">
              <label>Customer Name</label>
              <input
                type="text"
                placeholder="Customer Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={agreementReq}
                  onChange={(e) => setAgreementReq(e.target.checked)}
                />
                <span>Agreement required</span>
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={resaleReq}
                  onChange={(e) => setResaleReq(e.target.checked)}
                />
                <span>Resale certificate required</span>
              </label>
            </div>
          </div>
        </div>

        <div className="modal__footer">
          <div className="actions" style={{ gap: 8 }}>
            <button className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn--primary" onClick={submit} disabled={invite.isPending}>
              {invite.isPending ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
